import React, { useEffect } from "react";

import styled from "styled-components/macro";

import { useFamilyRegistrationContext } from "Signups/FamilyRegistration/FamilyRegistrationContext";
import ErrorMessage from "Signups/FamilyRegistration/Shared/ErrorMessage";
import RadioOption from "Signups/FamilyRegistration/Shared/RadioOption/RadioOption";
import useUpdateSetting from "Signups/FamilyRegistration/Shared/useUpdateSetting";
import { AnimatePresence, motion } from "framer-motion";

import FnrInput from "UI/FnrInput";
import FnrLoadingBricks from "UI/FnrLoadingBricks";

import validateUrl from "Utilities/validateUrl";

export default function GoToWebpage({
  disabled,
  updatingRadioButtons,
  setUpdatingRadioButtons,
}) {
  const { state } = useFamilyRegistrationContext();
  const {
    updateSetting,
    saving,
    error,
    clearError: clearToggleError,
  } = useUpdateSetting();
  const {
    updateSetting: updateInput,
    saving: inputSaving,
    error: inputError,
    clearError: clearInputError,
    setError,
  } = useUpdateSetting();

  const [url, setUrl] = React.useState("");
  const [buttonText, setButtonText] = React.useState("");
  const [validationError, setValidationError] = React.useState("");

  const enabled =
    state?.familyRegistrationSettings?.finishedActionWebsite?.enabled;
  const urlValue =
    state?.familyRegistrationSettings?.finishedActionWebsite?.websiteUrl;
  const buttonTextValue =
    state?.familyRegistrationSettings?.finishedActionWebsite?.buttonText;

  useEffect(() => {
    setUrl(urlValue);
  }, [urlValue]);

  useEffect(() => {
    setButtonText(buttonTextValue);
  }, [buttonTextValue]);

  useEffect(
    function clearErrorWhenNotSelected() {
      if (enabled) return;
      clearInputError();
    },
    [clearInputError, enabled]
  );

  async function handleToggle() {
    if (updatingRadioButtons) return;
    clearInputError();
    setUpdatingRadioButtons(true);
    let postData = {
      setting: "finishedActionWebsite",
      value: !enabled,
    };
    await updateSetting(postData);
    setUpdatingRadioButtons(false);
  }

  async function saveData() {
    clearToggleError();

    // only save if it has changed
    if (url === urlValue && buttonText === buttonTextValue) return;

    let valid = validateUrl(url);
    if (!valid) {
      setError({ error: "Please enter a valid URL" });
      return;
    }

    let postData = {
      setting: "finishedActionWebsite",
      websiteUrl: url,
      buttonText,
    };
    await updateInput(postData);
  }

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      saveData();
    }
  };

  return (
    <div>
      <RadioOption
        selected={enabled}
        disabled={disabled || (updatingRadioButtons && !saving)}
        id={3}
        label="Take them to a new web page"
        onClick={handleToggle}
        loading={saving}
      >
        <AnimatePresence>
          {enabled && (
            <Form
              style={{ originY: "top" }}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FnrInput
                onBlur={saveData}
                simpleLabel={true}
                disabled={inputSaving}
                type="url"
                value={url}
                onChange={(val) => setUrl(val)}
                placeholder="URL"
                onKeyDown={handleEnter}
              />
              <FnrInput
                onBlur={saveData}
                simpleLabel={true}
                disabled={inputSaving}
                type="text"
                value={buttonText}
                onChange={(val) => setButtonText(val)}
                placeholder="Text for the button link"
                onKeyDown={handleEnter}
              />

              {inputSaving ? (
                <LoadingWrapper>
                  <FnrLoadingBricks show={inputSaving} />
                </LoadingWrapper>
              ) : null}
            </Form>
          )}
        </AnimatePresence>
      </RadioOption>

      <ErrorMessage error={error?.error || inputError?.error} />
    </div>
  );
}

const LoadingWrapper = styled.div`
  z-index: 1000;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  place-items: center;
  background-color: hsl(0 0% 100% / 0.5);
`;

const Form = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 520px;
  position: relative;
  overflow: hidden;
`;
